import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Container, Segment, Button, Form, Message } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import { formatUrl } from '@plone/volto/helpers/Api/Api';
import { getBaseUrl, flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import backSVG from '@plone/volto/icons/back.svg';

/**
 * Editor-facing panel, reachable from the object's "More" menu (see
 * MoreMenuLink.jsx), to set/change/remove the shared password directly in
 * Volto - previously only possible via collective_folderprotection's
 * classic-UI @@assignpassword form. Talks to the backend's
 * @password-protection PATCH action (cmf.ModifyPortalContent-gated).
 */
const PasswordProtectionManager = (props) => {
  const pathname = props.location?.pathname || props.pathname;
  const path = getBaseUrl(pathname).replace(/\/password-protection$/, '');
  const token = useSelector((state) => state.userSession.token);

  const [status, setStatus] = useState(null); // null while loading
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const authHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token],
  );

  const loadStatus = useCallback(async () => {
    const response = await fetch(formatUrl(`${path}/@password-protection`), {
      credentials: 'include',
      headers: authHeaders(),
    });
    const body = await response.json();
    setStatus(body);
    setReason(body.reason?.data || '');
  }, [path, authHeaders]);

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const handleSave = async (event) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const payload = { reason };
    if (password) {
      payload.password = password;
    }
    const response = await fetch(formatUrl(`${path}/@password-protection`), {
      method: 'PATCH',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (response.ok) {
      setPassword('');
      setMessage({ type: 'success', text: 'Saved.' });
      await loadStatus();
    } else {
      const body = await response.json().catch(() => ({}));
      setMessage({
        type: 'error',
        text: body.error === 'password_required'
          ? 'Enter a password first.'
          : 'Could not save.',
      });
    }
    setBusy(false);
  };

  const handleRemove = async () => {
    setBusy(true);
    setMessage(null);
    const response = await fetch(formatUrl(`${path}/@password-protection`), {
      method: 'PATCH',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify({ remove: true }),
    });
    if (response.ok) {
      setPassword('');
      setReason('');
      setMessage({ type: 'success', text: 'Protection removed.' });
      await loadStatus();
    } else {
      setMessage({ type: 'error', text: 'Could not remove protection.' });
    }
    setBusy(false);
  };

  return (
    <Container className="view-wrapper password-protection-manager">
      <Toolbar
        pathname={pathname}
        hideDefaultViewButtons
        inner={
          <Link to={getBaseUrl(flattenToAppURL(path))} className="item">
            <Icon name={backSVG} className="contents circled" size="30px" />
          </Link>
        }
      />
      <Segment.Group>
        <Segment secondary>
          <FormattedMessage
            id="Password Protection"
            defaultMessage="Password Protection"
          />
        </Segment>
        <Segment>
          {status === null && <p>Loading…</p>}
          {status && !status.behavior_enabled && (
            <Message warning>
              This content type does not have password protection enabled.
              Enable the &quot;Password Protected&quot; behavior for it under
              Site Setup → Dexterity Content Types first.
            </Message>
          )}
          {status && status.behavior_enabled && (
            <>
              <Message info={!status.protected} positive={status.protected}>
                {status.protected
                  ? 'This item (and everything under it) is currently password protected.'
                  : 'This item is not currently password protected.'}
              </Message>
              <Form onSubmit={handleSave}>
                <Form.Field>
                  <label>
                    {status.protected ? 'Change password' : 'Set password'}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={
                      status.protected
                        ? 'Leave empty to keep the current password'
                        : 'Password'
                    }
                    autoComplete="new-password"
                  />
                </Form.Field>
                <Form.Field>
                  <label>
                    Reason shown to visitors (optional, HTML allowed)
                  </label>
                  <textarea
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Ask the course lead for the password."
                  />
                </Form.Field>
                <Button primary type="submit" loading={busy} disabled={busy}>
                  Save
                </Button>
                {status.protected && (
                  <Button
                    type="button"
                    negative
                    basic
                    disabled={busy}
                    onClick={handleRemove}
                  >
                    Remove protection
                  </Button>
                )}
              </Form>
              {message && (
                <Message
                  positive={message.type === 'success'}
                  negative={message.type === 'error'}
                  style={{ marginTop: '1rem' }}
                >
                  {message.text}
                </Message>
              )}
            </>
          )}
        </Segment>
      </Segment.Group>
    </Container>
  );
};

export default PasswordProtectionManager;
